// `property_type` is a closed list; `property_subtype` and
// `marketing_badge` are free text on the API — SUBTYPES/MARKETING_BADGES
// below are only <datalist> suggestions, not enforced options
// (docs/real-estate/be/07-fe-handoff.md §4.1).
export const PROP_TYPES = ['Residential', 'Retail', 'Industrial', 'Office'];

export const SUBTYPES = {
  Residential: [
    'Condominium',
    'Service Apartment',
    'Terrace',
    'Semi-Detached',
    'Bungalow',
    'Townhouse',
  ],
  Retail: ['Shoplot', 'Retail Lot', 'Mall Unit'],
  Industrial: ['Factory', 'Warehouse', 'Light Industrial'],
  Office: ['Office Suite', 'Office Tower Floor', 'Co-working Space'],
};

export const MARKETING_BADGES = ['Selling Fast', 'Limited Unit', 'New Launch'];

export const STATUSES = {
  upcoming: 'Coming soon',
  open: 'Now open for booking',
  sold_out: 'Sold out',
  completed: 'Completed',
};

export const STATUS_STYLE = {
  upcoming:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300',
  open: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  sold_out: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
  completed:
    'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300',
};

// Hardcoded MYR-denominated buckets for the price-range filter — not
// currency-aware yet (see multicurrency note in botpress-information.md).
// 'min-max'; an open-ended tier ('1200001-') has no upper bound.
export const PRICE_TIERS = [
  ['', 'Any price'],
  ['100000-350000', 'RM 100,000 – 350,000'],
  ['350001-600000', 'RM 350,001 – 600,000'],
  ['600001-850000', 'RM 600,001 – 850,000'],
  ['850001-1200000', 'RM 850,001 – 1.2m'],
  ['1200001-', 'Above RM 1.2m'],
];

export const CURRENCIES = ['RM', 'USD', 'SGD'];

// A floor plan's `built_up_unit` (docs/real-estate/be/13-fe-update-2026-09-30.md
// §2) — the size is stored as typed in this unit, never converted.
export const SIZE_UNITS = ['sqft', 'sqm', 'sqyd', 'sqin', 'sqcm'];
export const DEFAULT_SIZE_UNIT = 'sqft';

export const APPT_TYPES = {
  viewing: 'Property Viewing',
  key_handover: 'Key Handover',
};

export const APPT_STATUSES = {
  requested: 'Requested',
  confirmed: 'Confirmed',
  rescheduled: 'Rescheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Amber, like sky below, is a theme CSS-variable scale that already swaps
// for dark mode — `dark:` overrides pick the inverted end of it (a cream
// fill with near-black text), so plain shades only.
export const APPT_STATUS_STYLE = {
  requested: 'bg-amber-100 text-amber-800',
  confirmed:
    'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  // The theme has no `blue` palette (colors are replaced) — `sky` is the
  // nearest defined one. Its 700 is a pale cyan; 800 is the readable text
  // shade, and the theme's CSS variables already swap for dark mode.
  rescheduled: 'bg-sky-100 text-sky-800',
  completed:
    'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100',
};

// Deterministic gradient background hues, keyed by hashIndex(property_id,
// HUES.length) (see helpers.js) — decorative placeholder shown on a
// project card when it has no cover image set yet.
export const HUES = [255, 205, 20, 160, 280, 35];

// Shared text input/select/textarea styling for realEstate forms — bare
// <input>/<select> pick up forced global styles from _base.scss that don't
// match this module's layout, same reason condoMngt's forms spell this out
// explicitly rather than relying on a shared `.input` class (none exists).
export const FIELD_CLASS =
  'bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg py-2 px-3 w-full focus:outline-none focus:ring-2 focus:ring-woot-500 focus:border-transparent transition-all text-slate-900 dark:text-slate-100';

// _base.scss's global `select {}` rule draws the dropdown arrow as a
// background image at `right -1rem center` with `bg-origin-content`, sized
// against a reserved `pr-6`, and also adds `mb-4 h-10`. FIELD_CLASS's `px-3`
// overrides that reserved space, pushing the arrow outside the box, and the
// unwanted `mb-4` bottom margin is never reset. SELECT_CLASS widens the
// right padding back past pr-6 and zeroes the margin so <select> renders
// correctly inside this module's layout.
export const SELECT_CLASS = `${FIELD_CLASS} pr-8 mb-0`;
